import { esgMap, materialPillarMap } from '@apps/materiality-tracker/utils';
import { ReactGoogleChartEvent } from 'react-google-charts/dist/types';
import variables from '../../../../css/variables.module.scss';
import {
  DoubleMaterialityAssessmentData,
  EESGCategory,
  MaterialPillar,
} from '@apps/materiality-tracker/api/materiality-assessment';
import { isScoreInRange } from './utils';
import ReactDOMServer from 'react-dom/server';

export enum ChartType {
  ScatterChart = 'ScatterChart',
  ColumnChart = 'ColumnChart',
  BiDirectionalBarChart = 'BiDirectionalBarChart',
}

export const chartTypeOptions = [
  {
    label: 'Scatter chart',
    value: ChartType.ScatterChart,
  },
  {
    label: 'Mirror chart',
    value: ChartType.BiDirectionalBarChart,
  },
  {
    label: 'Stacked bar chart',
    value: ChartType.ColumnChart,
  },
];

export enum LegendType {
  // Field name is 'esg' to match the data structure field (categories.esg),
  // but the category enum is EESGCategory which includes Economic, Environmental, Social, and Governance.
  // The 'esg' field name is kept for backward compatibility with existing data/API.
  EESG = 'esg',
  MaterialPillar = 'materialPillar',
  Materiality = 'materiality',
}

export const categoryOptions = [
  {
    label: 'Materiality',
    value: LegendType.Materiality,
  },
  {
    label: 'Pillars',
    value: LegendType.MaterialPillar,
  },
  {
    label: 'EESG',
    value: LegendType.EESG,
  },
];

export const getChartEvents = (clickHandler: (index: number) => void): ReactGoogleChartEvent[] => [
  {
    eventName: 'select',
    callback({ chartWrapper }) {
      const [selection] = chartWrapper.getChart().getSelection();
      if (!selection) {
        return;
      }
      clickHandler(selection.row);
    },
  },
];

export type ChartConfig = {
  code: LegendType;
  categories: Record<
    string,
    { label: string; defaultColor?: string; selectedColor?: string; min?: number; max?: number }
  >;
};

export const chartConfigs: Pick<ChartConfig, 'code' | 'categories'>[] = [
  {
    code: LegendType.EESG,
    categories: esgMap,
  },
  {
    code: LegendType.MaterialPillar,
    categories: materialPillarMap,
  },
  {
    code: LegendType.Materiality,
    categories: {
      high: { label: 'High relevance', min: 80 },
      medium: { label: 'Medium relevance', min: 30, max: 80 },
      low: { label: 'Low relevance', max: 30 },
    },
  },
];

const {
  ChartDark,
  ChartMedium,
  ChartLight,
  ChartBright,
  ChartPale,
  ChartPurple,
  ChartRed,
  SuccessMedium,
  WarningMedium,
  DangerMedium,
  BorderDefault,
  HeadingLight,
} = variables;

export const DEFAULT_CHART_COLORS = [ChartDark, ChartMedium, ChartLight, ChartBright, ChartPale, ChartPurple, ChartRed];

export const MATERIALITY_CHART_COLORS = [SuccessMedium, WarningMedium, DangerMedium];

export const chartStyles = {
  legend: {
    position: 'top',
    alignment: 'end',
    textStyle: { fontSize: 9 },
  },
  chartArea: {
    top: 40,
    bottom: 40,
    right: 0,
    width: '96%',
    height: 320,
    backgroundColor: {
      stroke: BorderDefault,
      strokeWidth: 1,
    },
  },
  axis: {
    titleTextStyle: {
      color: HeadingLight,
      fontSize: 11,
      bold: true,
      italic: false,
    },
    textPosition: 'none',
    baselineColor: 'transparent',
    gridlines: {
      count: 0,
    },
  },
};

export const CHART_TICKS = [0, 20, 40, 60, 80, 100];

export const getChartColumnColor = (
  topic: DoubleMaterialityAssessmentData,
  config: Pick<ChartConfig, 'code' | 'categories'>,
  category: LegendType,
) => {
  let indexColor;
  switch (category) {
    case LegendType.EESG:
      indexColor = Object.values(EESGCategory).findIndex((code) => topic.categories?.esg?.includes(code));
      return `color: ${DEFAULT_CHART_COLORS[indexColor ?? 0]}`;
    case LegendType.MaterialPillar:
      indexColor = Object.values(MaterialPillar).findIndex((code) => topic.categories?.materialPillar?.includes(code));
      return `color: ${DEFAULT_CHART_COLORS[indexColor ?? 0]}`;
    case LegendType.Materiality: {
      indexColor = Object.values(config.categories).findIndex(({ min, max }) => {
        return isScoreInRange({ score: topic.normalizedScore, min, max });
      });
      return `color: ${MATERIALITY_CHART_COLORS[indexColor ?? 0]}`;
    }
  }
};

/** Used by react-google-charts to render tooltips as HTML, expected to return a string */
export const renderDoubleMaterialityTooltip = (topic: DoubleMaterialityAssessmentData) => {
  const { name, financialRelativeScore, nonFinancialRelativeScore } = topic;
  return ReactDOMServer.renderToString(
    <div style={{ minWidth: 'max-content' }} className='p-2'>
      <strong>{name}</strong>
      <div>Financial: {financialRelativeScore ? `${financialRelativeScore.toFixed(2)}%` : 'n/a'}</div>
      <div>Impact: {nonFinancialRelativeScore ? `${nonFinancialRelativeScore.toFixed(2)}%` : 'n/a'}</div>
    </div>,
  );
};
